/**
 * Shared confirmation modal for the Recurring transactions page.
 *
 * Used by:
 *   - Run now (info variant, posts a transaction immediately)
 *   - Delete template (danger variant, red primary button)
 *   - End this recurring (danger-light variant, stops future runs)
 *
 * React Portal into document.body. Centered on viewport, backdrop
 * dims the page, Escape closes, backdrop click closes.
 *
 * Variant decides the icon color and primary button color:
 *   - info:   dark navy icon on light wash, dark ink primary button
 *   - danger: red icon, red destructive primary button
 *
 * Optional stat row shows a number + label (e.g. "$4,200.00 will
 * post to RBC Chequing"). Optional footerNote shows a small helper
 * line below the body (safer-alternative hints like "pause instead").
 */

import { useEffect, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Play, Trash2, Square } from "lucide-react";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  bgFade: "#F3F5F5",
  red: "#B42318",
  redWash: "#FEF3F2",
  navy: "#1E293B",
  navyWash: "#F1F5F9",
};

export type ConfirmModalVariant = "info" | "danger";
export type ConfirmModalIcon = "run" | "delete" | "end";

interface ConfirmModalProps {
  isOpen: boolean;
  variant: ConfirmModalVariant;
  icon?: ConfirmModalIcon;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  statValue?: string;
  statLabel?: string;
  footerNote?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmModal({
  isOpen,
  variant,
  icon = "run",
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  statValue,
  statLabel,
  footerNote,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isInfo = variant === "info";
  const Icon = icon === "delete" ? Trash2 : icon === "end" ? Square : Play;
  const iconColor = isInfo ? PALETTE.navy : PALETTE.red;
  const iconBg = isInfo ? PALETTE.navyWash : PALETTE.redWash;
  const confirmBtnStyle: CSSProperties = isInfo ? primaryBtnStyle : dangerBtnStyle;

  return createPortal(
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...iconWrapStyle, background: iconBg, color: iconColor }}>
          <Icon size={22} color={iconColor} strokeWidth={2} />
        </div>

        <h3 style={titleStyle}>{title}</h3>
        <div style={bodyStyle}>{description}</div>

        {statValue !== undefined && statLabel && (
          <div style={statRowStyle}>
            <div style={statValueStyle}>{statValue}</div>
            <div style={statLabelStyle}>{statLabel}</div>
          </div>
        )}

        {footerNote && <div style={footerNoteStyle}>{footerNote}</div>}

        <div style={actionsStyle}>
          <button type="button" style={ghostBtnStyle} onClick={onClose}>
            {cancelLabel}
          </button>
          <button type="button" style={confirmBtnStyle} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.42)",
  zIndex: 2500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};

const modalStyle: CSSProperties = {
  background: "#ffffff",
  borderRadius: 14,
  maxWidth: 440,
  width: "100%",
  padding: 26,
  boxShadow: "0 24px 48px -6px rgba(14, 26, 26, 0.3)",
};

const iconWrapStyle: CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
};

const titleStyle: CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: "0 0 8px",
  letterSpacing: "-0.01em",
};

const bodyStyle: CSSProperties = {
  fontSize: 13.5,
  color: PALETTE.ink3,
  lineHeight: 1.55,
  marginBottom: 22,
};

const statRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  background: PALETTE.bgFade,
  borderRadius: 8,
  margin: "14px 0 18px",
};

const statValueStyle: CSSProperties = {
  fontSize: 20,
  fontWeight: 800,
  color: PALETTE.ink,
  fontFamily: "ui-monospace, Menlo, monospace",
};

const statLabelStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink3,
  fontWeight: 500,
};

const footerNoteStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink4,
  marginBottom: 18,
  lineHeight: 1.55,
};

const actionsStyle: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
};

const ghostBtnStyle: CSSProperties = {
  background: "transparent",
  border: `1px solid ${PALETTE.line}`,
  color: PALETTE.ink2,
  fontSize: 13,
  fontWeight: 600,
  padding: "9px 16px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};

const primaryBtnStyle: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
  border: 0,
  fontSize: 13,
  fontWeight: 600,
  padding: "10px 18px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};

const dangerBtnStyle: CSSProperties = {
  background: PALETTE.red,
  color: "#ffffff",
  border: 0,
  fontSize: 13,
  fontWeight: 600,
  padding: "10px 18px",
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: "inherit",
};